import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getEntitlements,
  getLicenses,
  getProducts,
  getModules,
  getSeatAssignments,
  getSiteModuleOverrides,
  getLicenseTemplates,
  createLicenseTemplate,
  updateLicenseTemplate,
  deleteLicenseTemplate,
  setLicenseTemplateModule,
  createLicenseFromTemplate,
  setLicenseStatusAction,
  setLicenseModule,
  setSiteModuleOverride,
} from '../../services/licensing.service';
import type {
  LicenseFilters,
  LicenseFromTemplatePayload,
  LicenseStatusAction,
  LicenseTemplateWritePayload,
} from '../../services/licensing.service';

export const licensingKeys = {
  all: ['licensing'] as const,
  entitlements: () => [...licensingKeys.all, 'entitlements'] as const,
  licenses: () => [...licensingKeys.all, 'licenses'] as const,
  licenseList: (filters: LicenseFilters) =>
    [...licensingKeys.licenses(), filters] as const,
  products: () => [...licensingKeys.all, 'products'] as const,
  modules: () => [...licensingKeys.all, 'modules'] as const,
  seats: (licenseId: number) =>
    [...licensingKeys.all, 'seats', licenseId] as const,
  siteOverrides: (licenseId: number, siteId: number) =>
    [...licensingKeys.all, 'siteOverrides', licenseId, siteId] as const,
  templates: () => [...licensingKeys.all, 'templates'] as const,
};

export function useEntitlements() {
  return useQuery({
    queryKey: licensingKeys.entitlements(),
    queryFn: getEntitlements,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLicenses(filters: LicenseFilters = {}) {
  return useQuery({
    queryKey: licensingKeys.licenseList(filters),
    queryFn: () => getLicenses(filters),
    placeholderData: (prev) => prev,
  });
}

export function useProducts() {
  return useQuery({
    queryKey: licensingKeys.products(),
    queryFn: getProducts,
    staleTime: 30 * 60 * 1000,
  });
}

export function useModules() {
  return useQuery({
    queryKey: licensingKeys.modules(),
    queryFn: getModules,
    staleTime: 30 * 60 * 1000,
  });
}

export function useSeatAssignments(licenseId: number) {
  return useQuery({
    queryKey: licensingKeys.seats(licenseId),
    queryFn: () => getSeatAssignments(licenseId),
    enabled: !!licenseId,
  });
}

export function useSiteModuleOverrides(licenseId: number, siteId: number) {
  return useQuery({
    queryKey: licensingKeys.siteOverrides(licenseId, siteId),
    queryFn: () => getSiteModuleOverrides(licenseId, siteId),
    enabled: !!licenseId && !!siteId,
  });
}

// ── License Templates (ADR 104 §18) ──────────────────────────────────────────

export function useLicenseTemplates(enabled = true) {
  return useQuery({
    queryKey: licensingKeys.templates(),
    queryFn: getLicenseTemplates,
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

function invalidateAllLicensing(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: licensingKeys.all });
}

export function useCreateLicenseTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LicenseTemplateWritePayload) =>
      createLicenseTemplate(payload),
    onSuccess: () => invalidateAllLicensing(queryClient),
  });
}

export function useUpdateLicenseTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Partial<LicenseTemplateWritePayload>;
    }) => updateLicenseTemplate(id, payload),
    onSuccess: () => invalidateAllLicensing(queryClient),
  });
}

export function useDeleteLicenseTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteLicenseTemplate(id),
    onSuccess: () => invalidateAllLicensing(queryClient),
  });
}

export function useSetLicenseTemplateModule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      templateId,
      module_code,
      enabled_by_default,
    }: {
      templateId: string;
      module_code: string;
      enabled_by_default: boolean;
    }) =>
      setLicenseTemplateModule(templateId, module_code, enabled_by_default),
    onSuccess: () => invalidateAllLicensing(queryClient),
  });
}

export function useCreateLicenseFromTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LicenseFromTemplatePayload) =>
      createLicenseFromTemplate(payload),
    onSuccess: () => invalidateAllLicensing(queryClient),
  });
}

export function useSetLicenseStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      licenseId,
      action,
      body,
    }: {
      licenseId: number;
      action: LicenseStatusAction;
      body?: Record<string, unknown>;
    }) => setLicenseStatusAction(licenseId, action, body ?? {}),
    onSuccess: () => invalidateAllLicensing(queryClient),
  });
}

export function useSetLicenseModule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      licenseId,
      module_code,
      enabled,
    }: {
      licenseId: number;
      module_code: string;
      enabled: boolean;
    }) => setLicenseModule(licenseId, module_code, enabled),
    onSuccess: () => invalidateAllLicensing(queryClient),
  });
}

export function useSetSiteModuleOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      licenseId,
      siteId,
      module_code,
      enabled,
    }: {
      licenseId: number;
      siteId: number;
      module_code: string;
      enabled: boolean;
    }) => setSiteModuleOverride(licenseId, siteId, module_code, enabled),
    onSuccess: () => invalidateAllLicensing(queryClient),
  });
}
